/**
 * P53 — ODM HTML ErrorReport Viewer
 * ---------------------------------
 * Pulls the list of *ErrorReport*.html files from ODM's outbound directory
 * (CERT or PROD) and lets AZAI super-admins inspect ODM's compliance-check
 * diagnostics directly in the dashboard. Replaces SSH-then-cat-the-file.
 *
 * Renders by default inside a sandboxed iframe (no scripts, no parent
 * window access). A "Switch to inline" toggle DOMPurify-sanitizes the HTML
 * for a cleaner inline read.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import api from '../../services/api';
import { formatDateTime } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  ArrowClockwise,
  ArrowSquareOut,
  Copy,
  DownloadSimple,
  File,
  Warning,
  ArrowRight,
} from '@phosphor-icons/react';

const ENV_OPTIONS = [
  { value: 'cert', label: 'CERT', tone: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'prod', label: 'PROD', tone: 'bg-rose-100 text-rose-800 border-rose-300' },
];

const formatBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// DOMPurify config — strip every script vector but keep tables/styles so
// ODM's color-coded error blocks render correctly inline.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'html', 'head', 'body', 'meta', 'title', 'style',
    'div', 'span', 'p', 'br', 'hr',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
    'strong', 'em', 'b', 'i', 'u', 'code', 'pre', 'small',
    'a', 'font', 'center',
  ],
  ALLOWED_ATTR: [
    'style', 'class', 'id',
    'colspan', 'rowspan', 'align', 'valign', 'width', 'height',
    'bgcolor', 'cellpadding', 'cellspacing', 'border',
    'href', 'target', 'rel', 'title',
  ],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'link'],
  FORBID_ATTR: [
    'onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout',
    'onfocus', 'onblur', 'onsubmit', 'onchange', 'onkeydown', 'onkeyup',
  ],
  WHOLE_DOCUMENT: true,
  ALLOW_DATA_ATTR: false,
};

function ReportViewerDialog({ report, env, onClose }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStartedAt, setLoadingStartedAt] = useState(null);
  const [, setNow] = useState(Date.now());
  const [error, setError] = useState(null);
  const [renderMode, setRenderMode] = useState('iframe'); // 'iframe' | 'inline'
  const [relatedBatch, setRelatedBatch] = useState(report?.related_batch || null);

  useEffect(() => {
    if (!report) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadingStartedAt(Date.now());
    setError(null);
    setContent(null);
    api
      .get(`/odm-sftp/error-reports/${encodeURIComponent(report.filename)}`, {
        params: env ? { env } : {},
      })
      .then((res) => {
        if (cancelled) return;
        setContent(res.data?.content || '');
        setRelatedBatch(res.data?.related_batch || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || err.message || 'Failed to load report');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setLoadingStartedAt(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [report, env]);

  // Tick elapsed-time hint every second while loading the report content.
  useEffect(() => {
    if (!loading) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [loading]);

  const elapsedSeconds = useMemo(() => {
    if (!loading || !loadingStartedAt) return 0;
    return Math.max(0, Math.floor((Date.now() - loadingStartedAt) / 1000));
  }, [loading, loadingStartedAt]);

  const sanitizedHtml = useMemo(() => {
    if (!content) return '';
    return DOMPurify.sanitize(content, PURIFY_CONFIG);
  }, [content]);

  const handleCopy = useCallback(async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      toast.success('ErrorReport copied to clipboard');
    } catch {
      toast.error('Clipboard copy failed');
    }
  }, [content]);

  const handleDownload = useCallback(() => {
    if (!content || !report) return;
    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = report.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [content, report]);

  if (!report) return null;

  const envLabel = (env || 'active').toUpperCase();
  const envTone = env === 'prod' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800';

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent
        className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="error-report-viewer-dialog"
      >
        <DialogHeader className="border-b border-slate-200 px-6 pt-6 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Warning size={18} weight="duotone" className="text-rose-500 shrink-0" />
                <span className="truncate font-mono">{report.filename}</span>
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                <Badge className={`${envTone} text-[10px]`} data-testid="error-report-env-badge">
                  {envLabel}
                </Badge>
                <span>·</span>
                <span>{formatDateTime(report.modified)}</span>
                <span>·</span>
                <span>{formatBytes(report.size)}</span>
                {relatedBatch && (
                  <>
                    <span>·</span>
                    <Link
                      to={`/odm/submissions/${encodeURIComponent(relatedBatch.batch_id)}`}
                      className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 underline"
                      data-testid="error-report-related-batch-link"
                    >
                      Related batch: {relatedBatch.batch_id?.slice(0, 8)}…
                      <ArrowRight size={11} />
                    </Link>
                  </>
                )}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setRenderMode('iframe')}
                  className={`px-2.5 py-1 text-xs rounded-md transition ${
                    renderMode === 'iframe'
                      ? 'bg-white text-slate-900 shadow-sm font-medium'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="error-report-mode-iframe-btn"
                >
                  Sandboxed
                </button>
                <button
                  type="button"
                  onClick={() => setRenderMode('inline')}
                  className={`px-2.5 py-1 text-xs rounded-md transition ${
                    renderMode === 'inline'
                      ? 'bg-white text-slate-900 shadow-sm font-medium'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="error-report-mode-inline-btn"
                >
                  Inline
                </button>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopy}
                disabled={!content}
                data-testid="error-report-copy-btn"
              >
                <Copy size={14} className="mr-1" />
                Copy
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownload}
                disabled={!content}
                data-testid="error-report-download-btn"
              >
                <DownloadSimple size={14} className="mr-1" />
                Download
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden bg-slate-50">
          {loading && (
            <div
              className="h-full flex items-center justify-center text-slate-500 text-sm"
              data-testid="error-report-loading"
            >
              <div className="inline-flex flex-col items-center gap-3">
                <ArrowClockwise size={24} className="animate-spin text-indigo-500" />
                <span>
                  Loading ErrorReport from ODM SFTP{env ? ` (${env.toUpperCase()})` : ''}…
                </span>
                {elapsedSeconds >= 3 && (
                  <span className="text-[11px] text-slate-400">
                    {elapsedSeconds}s elapsed · first cold fetch can take 30–40s
                  </span>
                )}
              </div>
            </div>
          )}
          {!loading && error && (
            <div
              className="h-full flex items-center justify-center px-8"
              data-testid="error-report-error"
            >
              <div className="text-center max-w-md">
                <Warning size={32} weight="duotone" className="text-rose-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-slate-900 mb-1">Failed to load ErrorReport</p>
                <p className="text-xs text-slate-600 break-words">{error}</p>
              </div>
            </div>
          )}
          {!loading && !error && content && renderMode === 'iframe' && (
            <iframe
              key={report.filename}
              title={`ErrorReport ${report.filename}`}
              sandbox=""
              srcDoc={sanitizedHtml}
              className="w-full h-full border-0 bg-white"
              data-testid="error-report-iframe"
            />
          )}
          {!loading && !error && content && renderMode === 'inline' && (
            <div
              className="h-full overflow-auto bg-white p-6 text-sm"
              data-testid="error-report-inline"
              // sanitized via DOMPurify above — safe to mount as HTML
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ErrorReportsTab() {
  // Start with `null` so the very first fetch sends no `env` query param —
  // backend then resolves the currently-active global env. We seed local
  // state from the response so the toggle reflects reality. (P53.4)
  const [env, setEnv] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStartedAt, setLoadingStartedAt] = useState(null);
  const [, setNow] = useState(Date.now());
  const [error, setError] = useState(null);
  const [path, setPath] = useState(null);
  const [activeReport, setActiveReport] = useState(null);

  // Tick every second while loading so the elapsed-time hint refreshes.
  // First SFTP cold fetch can take 30–40s; users need feedback.
  useEffect(() => {
    if (!loading) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [loading]);

  const elapsedSeconds = useMemo(() => {
    if (!loading || !loadingStartedAt) return 0;
    return Math.max(0, Math.floor((Date.now() - loadingStartedAt) / 1000));
  }, [loading, loadingStartedAt]);

  const fetchReports = useCallback(async (envOverride) => {
    setLoading(true);
    setLoadingStartedAt(Date.now());
    setError(null);
    try {
      const params = {};
      const target = envOverride ?? env;
      if (target) params.env = target;
      const res = await api.get('/odm-sftp/error-reports', { params });
      if (!res.data?.success) {
        setError(res.data?.error || 'Failed to list ErrorReports');
        setReports([]);
        setPath(res.data?.path || null);
        // Even on failure, sync the env from the response so the toggle
        // reflects which env the backend actually resolved.
        if (res.data?.environment) setEnv(res.data.environment);
        return;
      }
      setReports(res.data.reports || []);
      setPath(res.data.path || null);
      if (res.data.environment) setEnv(res.data.environment);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Network error');
      setReports([]);
    } finally {
      setLoading(false);
      setLoadingStartedAt(null);
    }
  }, [env]);

  // Initial fetch — let backend pick the active env.
  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnvSwitch = useCallback((nextEnv) => {
    if (nextEnv === env || loading) return;
    setEnv(nextEnv);
    fetchReports(nextEnv);
  }, [env, loading, fetchReports]);

  const handleRefresh = useCallback(() => {
    fetchReports();
  }, [fetchReports]);

  return (
    <div className="space-y-4" data-testid="error-reports-tab">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600">Environment:</span>
            <div
              className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50"
              data-testid="error-report-env-toggle"
            >
              {ENV_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleEnvSwitch(opt.value)}
                  disabled={loading}
                  className={`px-3 py-1 text-xs rounded-md transition font-medium ${
                    env === opt.value
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                      : 'text-slate-500 hover:text-slate-700'
                  } ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  data-testid={`error-report-env-${opt.value}-btn`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {path && (
              <span
                className="text-[11px] font-mono text-slate-400 truncate max-w-[420px]"
                title={path}
              >
                {path}
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            data-testid="error-reports-refresh-btn"
          >
            <ArrowClockwise size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div
        className="bg-white border border-slate-200 rounded-xl overflow-hidden"
        data-testid="error-reports-table-wrapper"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-600 tracking-wider">
              <tr>
                <th className="px-4 py-3">Filename</th>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3 text-right">Size</th>
                <th className="px-4 py-3">Related Batch</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                    <div className="inline-flex flex-col items-center gap-2">
                      <ArrowClockwise size={20} className="animate-spin text-indigo-500" />
                      <span>
                        Loading ErrorReports from ODM SFTP
                        {env ? ` (${env.toUpperCase()})` : ''}…
                      </span>
                      {elapsedSeconds >= 3 && (
                        <span className="text-[11px] text-slate-400">
                          {elapsedSeconds}s elapsed · first cold fetch can take 30–40s
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <div className="inline-flex flex-col items-center gap-2 text-rose-700">
                      <Warning size={24} weight="duotone" />
                      <p className="text-sm font-semibold">Failed to list ErrorReports</p>
                      <p className="text-xs text-slate-600 max-w-md break-words">{error}</p>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && !error && reports.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-12 text-center text-slate-400"
                    data-testid="error-reports-empty"
                  >
                    No HTML ErrorReports in {(env || 'active').toUpperCase()} outbound right
                    now. ODM only emits these when a 270/837P file fails compliance checks —
                    clean envelopes pass straight through to 999 and 277CA.
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                reports.map((r) => (
                  <tr
                    key={`${env}-${r.filename}`}
                    className="hover:bg-rose-50/30"
                    data-testid={`error-report-row-${r.filename}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-slate-700 max-w-[420px]">
                      <div className="flex items-center gap-2">
                        <File size={14} className="text-rose-500 shrink-0" />
                        <span className="truncate" title={r.filename}>
                          {r.filename}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {formatDateTime(r.modified)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-500">
                      {formatBytes(r.size)}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {r.related_batch ? (
                        <Link
                          to={`/odm/submissions/${encodeURIComponent(r.related_batch.batch_id)}`}
                          className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 underline font-mono"
                          data-testid={`error-report-batch-link-${r.related_batch.batch_id}`}
                        >
                          {r.related_batch.batch_id?.slice(0, 8)}…
                          <ArrowSquareOut size={11} />
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setActiveReport(r)}
                        data-testid={`error-report-view-btn-${r.filename}`}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {activeReport && (
        <ReportViewerDialog
          report={activeReport}
          env={env}
          onClose={() => setActiveReport(null)}
        />
      )}
    </div>
  );
}
